import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Typography, Form, Input, Button, message, Card as AntCard } from 'antd';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

// Type workaround for Card component
const Card = AntCard as any;

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

export const Contact: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      console.log('Form values:', values);
      await new Promise(resolve => setTimeout(resolve, 1500));
      message.success('Thank you! We will get back to you soon.');
      form.resetFields();
    } catch (error) {
      message.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <Helmet>
        <title>Contact Us - QodeAris | Get in Touch with Our Team</title>
        <meta name="description" content="Contact QodeAris for inquiries about DhartiNetra and our agricultural IoT solutions. We're here to help farmers with innovative technology." />
        <meta name="keywords" content="contact QodeAris, agricultural technology support, DhartiNetra inquiries, IoT solutions contact, farming technology help" />
      </Helmet>
      <Navbar />
      <main id="main-content" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Title level={1} className="text-5xl md:text-6xl font-bold mb-6">
              {t('contact.title')}
              <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent"> {t('contact.highlight')}</span>
            </Title>
            <Paragraph className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              {t('contact.subtitle')}
            </Paragraph>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="shadow-xl">
                <Title level={3} className="mb-6">Send us a Message</Title>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={onFinish}
                  requiredMark="optional"
                >
                  <Form.Item
                    label="Full Name"
                    name="name"
                    rules={[{ required: true, message: 'Please enter your name' }]}
                  >
                    <Input
                      size="large"
                      placeholder="Your name"
                      aria-label="Full name"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Email Address"
                    name="email"
                    rules={[
                      { required: true, message: 'Please enter your email' },
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="your.email@example.com"
                      aria-label="Email address"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Phone Number"
                    name="phone"
                    rules={[{ required: true, message: 'Please enter your phone number' }]}
                  >
                    <Input
                      size="large"
                      placeholder="+91 98765 43210"
                      aria-label="Phone number"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Organization (Optional)"
                    name="organization"
                  >
                    <Input
                      size="large"
                      placeholder="Your organization name"
                      aria-label="Organization name"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Message"
                    name="message"
                    rules={[{ required: true, message: 'Please enter your message' }]}
                  >
                    <TextArea
                      rows={5}
                      placeholder="Tell us about your requirements or questions..."
                      aria-label="Message"
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      loading={loading}
                      icon={<Send className="w-5 h-5" aria-hidden="true" />}
                      className="w-full bg-green-600 hover:bg-green-700"
                      aria-label="Send message"
                    >
                      Send Message
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="space-y-6"
            >
              <Card className="shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-green-600 dark:text-green-400" aria-hidden="true" />
                  </div>
                  <div>
                    <Title level={4} className="mb-2">Email Us</Title>
                    <a href="mailto:hello@qodearis.com" className="text-green-600 hover:text-green-700 text-lg">
                      hello@qodearis.com
                    </a>
                    <Paragraph className="text-gray-600 dark:text-gray-300 mt-2 mb-0">
                      We typically respond within 24 hours
                    </Paragraph>
                  </div>
                </div>
              </Card>

              <Card className="shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-blue-600 dark:text-blue-400" aria-hidden="true" />
                  </div>
                  <div>
                    <Title level={4} className="mb-2">Call Us</Title>
                    <a href="tel:+919822628174" className="text-blue-600 hover:text-blue-700 text-lg">
                      +91 98226 28174
                    </a>
                    <Paragraph className="text-gray-600 dark:text-gray-300 mt-2 mb-0">
                      Monday - Saturday, 9:00 AM - 6:00 PM IST
                    </Paragraph>
                  </div>
                </div>
              </Card>

              <Card className="shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-purple-600 dark:text-purple-400" aria-hidden="true" />
                  </div>
                  <div>
                    <Title level={4} className="mb-2">Visit Us</Title>
                    <Text className="text-gray-700 dark:text-gray-300">
                      3rd Floor, Hashtag<br />
                      Hinjawadi Phase 1, Maan Road<br />
                      Pune – 411057, Maharashtra, India
                    </Text>
                  </div>
                </div>
              </Card>

              <Card className="bg-gradient-to-r from-green-600 to-emerald-600 text-white border-0 shadow-lg">
                <Title level={4} className="text-white mb-3">Partnership Opportunities</Title>
                <Paragraph className="text-white/90 mb-4">
                  Interested in partnering with QodeAris? We work with FPOs, NGOs, agribusinesses, 
                  and government initiatives to bring DhartiNetra to more farmers.
                </Paragraph>
                <a href="mailto:hello@qodearis.com?subject=Partnership Inquiry">
                  <button className="bg-white text-green-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                    Explore Partnerships
                  </button>
                </a>
              </Card>

              <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                <Title level={4} className="mb-3">Website</Title>
                <a 
                  href="https://www.qodearis.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 text-lg font-semibold"
                >
                  www.qodearis.com
                </a>
              </Card>
            </motion.div>
          </div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-16"
          >
            <Card className="shadow-xl">
              <Title level={3} className="mb-6 text-center">Frequently Asked Questions</Title>
              <div className="space-y-6">
                {[
                  {
                    q: 'What is the best way to reach you?',
                    a: 'Email us at hello@qodearis.com or call +91 98226 28174 during business hours.'
                  },
                  {
                    q: 'Do you offer product demonstrations?',
                    a: 'Yes! We provide free demonstrations of DhartiNetra. Contact us to schedule one.'
                  },
                  {
                    q: 'What are your office hours?',
                    a: 'Monday to Saturday, 9:00 AM - 6:00 PM IST. We respond to emails within 24 hours.'
                  },
                  {
                    q: 'Do you work with FPOs and cooperatives?',
                    a: 'Absolutely! We have special partnership programs for FPOs, NGOs, and agricultural cooperatives.'
                  },
                ].map((faq, index) => (
                  <div key={index} className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-0">
                    <Title level={5} className="mb-2">{faq.q}</Title>
                    <Text className="text-gray-600 dark:text-gray-300">{faq.a}</Text>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>

          {/* Office Hours */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-8"
          >
            <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
              <div className="text-center">
                <Title level={4} className="mb-4">Office Hours</Title>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto">
                  <div>
                    <Text className="font-semibold block mb-1">Monday - Friday</Text>
                    <Text className="text-gray-600 dark:text-gray-300">9:00 AM - 6:00 PM IST</Text>
                  </div>
                  <div>
                    <Text className="font-semibold block mb-1">Saturday</Text>
                    <Text className="text-gray-600 dark:text-gray-300">9:00 AM - 2:00 PM IST</Text>
                  </div>
                  <div>
                    <Text className="font-semibold block mb-1">Sunday</Text>
                    <Text className="text-gray-600 dark:text-gray-300">Closed</Text>
                  </div>
                  <div>
                    <Text className="font-semibold block mb-1">Email Response</Text>
                    <Text className="text-gray-600 dark:text-gray-300">Within 24 hours</Text>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
